import { createStyles, makeStyles } from '@material-ui/styles';
import { GraphViewerApp } from 'jupiter-graph-viewer';
import React from 'react';
import JsonView from 'react-json-view';
import { JupiterTheme } from '../../theme/types';
import { QueryResultsChange, PaginationSortingProps } from '../../types';
import QueryResultTable from './QueryResultTable';
import RawJsonView from './RawJsonView';
import { ResultFormat } from './types';
import {
  J1QLListData,
  J1QLResultData,
  J1QLTreeData,
} from '../../../types/J1QL';

const useStyles = makeStyles((theme: JupiterTheme) =>
  createStyles({
    queryResultTable: {
      display: 'grid',
      gridTemplateRows: '1fr',
      height: '100%',
    },
    jsonView: {
      background: 'rgba(239, 240, 245, 0.4)',
      padding: theme.spacing(1.5, 3, 3),
      '& .pretty-json-container *': {
        fontFamily: `'Ubuntu Mono', monospace`,
      },
    },
  })
);

interface Props {
  classes?: Record<'graphView', string>;
  query: string;
  format: ResultFormat;
  result: J1QLResultData;
  onResultsChange?: (changes: QueryResultsChange[]) => void;
  paginationProps?: PaginationSortingProps;
  refetch?: VoidFunction;
  disableRowClick?: boolean;
  useRowDataForEntityDrawer?: boolean;
  noDataEdit?: boolean;
  parentSelectedRowID?: string | null;
  parentSetSelectedRowID?: (id: string | null) => void;
}

const ResultForFormat = ({
  query,
  format,
  result,
  onResultsChange,
  refetch,
  paginationProps,
  ...props
}: Props) => {
  const classes = useStyles();
  let component: JSX.Element;

  const { type } = result;

  const resultEdges =
    type === 'tree' ? (result as J1QLTreeData).data?.edges || [] : [];

  const resultVertices =
    type === 'tree'
      ? (result as J1QLTreeData).data?.vertices || []
      : (result as J1QLListData).data!;

  switch (format) {
    case ResultFormat.Table:
      component = (
        <div className={classes.queryResultTable}>
          <QueryResultTable
            result={result as J1QLListData}
            query={query}
            onResultsChange={onResultsChange}
            paginationProps={paginationProps}
            {...props}
          />
        </div>
      );
      break;
    case ResultFormat.PrettyJson:
      component = (
        <div className={classes.jsonView}>
          <JsonView src={result} name={null} groupArraysAfterLength={10} />
        </div>
      );
      break;
    case ResultFormat.Graph:
      component = (
        <GraphViewerApp
          edges={resultEdges}
          vertices={resultVertices}
          query={query}
          onUpdateGraph={refetch ? refetch : undefined}
          paginationProps={paginationProps}
        />
      );
      break;
    case ResultFormat.RawJson:
      component = <RawJsonView result={result} />;
      break;
    default:
      component = <RawJsonView result={result} />;
  }

  return component;
};

export default ResultForFormat;
